import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL, getAuthHeaders, formatDateTime, feetToMeters } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CheckpointMap } from "../components/Map/CheckpointMap";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Building2,
  Users,
  MapPin,
  Route,
  Radio,
  ClipboardCheck,
  TrendingUp,
  Clock,
  Maximize2,
  XCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { MapContainer, Marker, Circle, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import GoogleTileLayer from "../components/Map/GoogleTileLayer";

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

export const Dashboard = () => {
  const { t } = useTranslation();
  const { user, canManage } = useAuth();
  const [stats, setStats] = useState(null);
  const [checkpoints, setCheckpoints] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [sessionDetails, setSessionDetails] = useState({});
  const [expandedMap, setExpandedMap] = useState(null);
  const [closingSessionId, setClosingSessionId] = useState(null);

  const refreshStats = async () => {
    try {
      const statsRes = await axios.get(`${API_URL}/stats/dashboard`, {
        headers: getAuthHeaders(),
      });
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error refreshing stats:", error);
    }
  };

  const handleForceEndSession = async (sessionId) => {
    if (!window.confirm(t("pages.dashboard.confirmCloseSession"))) return;
    setClosingSessionId(sessionId);
    try {
      await axios.post(
        `${API_URL}/patrol/sessions/${sessionId}/end`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(t("pages.dashboard.sessionClosed"));
      setModalData((prev) => prev.filter((s) => s.id !== sessionId));
      await refreshStats();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || t("pages.dashboard.sessionCloseError")
      );
    } finally {
      setClosingSessionId(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, checkpointsRes, logsRes] = await Promise.all([
          axios.get(`${API_URL}/stats/dashboard`, { headers: getAuthHeaders() }),
          axios.get(`${API_URL}/checkpoints`, { headers: getAuthHeaders() }),
          axios.get(`${API_URL}/patrol/logs`, { headers: getAuthHeaders() }),
        ]);
        setStats(statsRes.data);
        setCheckpoints(Array.isArray(checkpointsRes.data) ? checkpointsRes.data : []);
        const logs = Array.isArray(logsRes.data) ? logsRes.data : [];
        setRecentLogs(logs.slice(0, 10));
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleTileClick = async (title) => {
    setModalOpen(true);
    setModalTitle(title);
    setModalLoading(true);
    setModalData([]);

    try {
      let endpoint = "";
      let dataKey = "";

      switch (title) {
        case t("pages.dashboard.companies"):
          endpoint = `${API_URL}/companies`;
          dataKey = "companies";
          break;
        case t("pages.dashboard.users"):
          endpoint = `${API_URL}/users`;
          dataKey = "users";
          break;
        case t("pages.dashboard.checkpoints"):
          endpoint = `${API_URL}/checkpoints`;
          dataKey = "checkpoints";
          break;
        case t("pages.dashboard.routes"):
          endpoint = `${API_URL}/routes`;
          dataKey = "routes";
          break;
        case t("pages.dashboard.activeSessions"):
          endpoint = `${API_URL}/patrol/sessions/all-active`;
          dataKey = "sessions";
          const sessionsRes = await axios.get(endpoint, { headers: getAuthHeaders() });
          const sessions = Array.isArray(sessionsRes.data) ? sessionsRes.data : [];

          // Preferir datos enriquecidos del backend; completar si faltan
          const detailsMap = {};
          await Promise.all(
            sessions.map(async (session) => {
              if (session.user_name && session.route_name) {
                detailsMap[session.id] = {
                  sessionId: session.id,
                  userName: session.user_name,
                  routeName: session.route_name,
                  totalCheckpoints:
                    session.total_checkpoints ??
                    session.completed_checkpoints?.length ??
                    0,
                };
                return;
              }
              try {
                const [userRes, routeRes] = await Promise.all([
                  axios
                    .get(`${API_URL}/users/${session.user_id}`, {
                      headers: getAuthHeaders(),
                    })
                    .catch(() => ({ data: { name: t("pages.dashboard.unknown") } })),
                  axios
                    .get(`${API_URL}/routes/${session.route_id}`, {
                      headers: getAuthHeaders(),
                    })
                    .catch(() => ({
                      data: { name: t("pages.dashboard.unknownRoute"), checkpoint_ids: [] },
                    })),
                ]);
                detailsMap[session.id] = {
                  sessionId: session.id,
                  userName: userRes.data.name,
                  routeName: routeRes.data.name,
                  totalCheckpoints: routeRes.data.checkpoint_ids?.length || 0,
                };
              } catch (error) {
                detailsMap[session.id] = {
                  sessionId: session.id,
                  userName: t("pages.dashboard.unknown"),
                  routeName: t("pages.dashboard.unknownRoute"),
                  totalCheckpoints: 0,
                };
              }
            })
          );

          setSessionDetails(detailsMap);
          setModalData(sessions);
          setModalLoading(false);
          return;
        case t("pages.dashboard.guardsOnShift"):
          endpoint = `${API_URL}/time-records/guards-on-shift`;
          dataKey = "guards";
          break;
        case t("pages.dashboard.patrolsToday"):
          const today = new Date().toISOString().split("T")[0];
          endpoint = `${API_URL}/patrol/logs?start_date=${today}&end_date=${today}`;
          dataKey = "logs";
          // Obtener logs y luego obtener nombres de usuarios y checkpoints
          const logsRes = await axios.get(endpoint, { headers: getAuthHeaders() });
          const logs = Array.isArray(logsRes.data) ? logsRes.data : [];
          
          // Enriquecer logs con nombres
          const enrichedLogs = await Promise.all(
            logs.map(async (log) => {
              try {
                const [userRes, checkpointRes] = await Promise.all([
                  axios
                    .get(`${API_URL}/users/${log.user_id}`, {
                      headers: getAuthHeaders(),
                    })
                    .catch(() => ({ data: { name: "Desconocido" } })),
                  log.checkpoint_id
                    ? axios
                        .get(`${API_URL}/checkpoints/${log.checkpoint_id}`, {
                          headers: getAuthHeaders(),
                        })
                        .catch(() => ({ data: { name: "-" } }))
                    : Promise.resolve({ data: { name: "-" } }),
                ]);
                return {
                  ...log,
                  user_name: userRes.data.name,
                  checkpoint_name: checkpointRes.data.name,
                };
              } catch (error) {
                return {
                  ...log,
                  user_name: "Desconocido",
                  checkpoint_name: "-",
                };
              }
            })
          );
          
          setModalData(enrichedLogs);
          setModalLoading(false);
          return;
        default:
          setModalLoading(false);
          return;
      }

      if (title !== t("pages.dashboard.activeSessions") && title !== t("pages.dashboard.patrolsToday") && endpoint) {
        const response = await axios.get(endpoint, { headers: getAuthHeaders() });
        setModalData(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error(`Error fetching ${title} details:`, error);
      setModalData([]);
    } finally {
      if (title !== t("pages.dashboard.activeSessions") && title !== t("pages.dashboard.patrolsToday")) {
        setModalLoading(false);
      }
    }
  };

  const renderModalContent = () => {
    if (modalLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (!Array.isArray(modalData) || modalData.length === 0) {
      return (
        <div className="text-center py-8 text-slate-500">
          No hay datos disponibles
        </div>
      );
    }

    switch (modalTitle) {
      case t("nav.companies"):
        return (
          <div className="overflow-auto max-h-[60vh] relative">
            <table className="w-full caption-bottom text-sm">
              <TableHeader>
                <TableRow className="sticky top-0 z-10 bg-white shadow-sm">
                  <TableHead className="bg-white">{t("common.name")}</TableHead>
                  <TableHead className="bg-white">{t("common.address")}</TableHead>
                  <TableHead className="bg-white">{t("common.phone")}</TableHead>
                  <TableHead className="bg-white">{t("common.email")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {modalData.map((company) => (
                  <TableRow key={company.id}>
                    <TableCell className="font-medium">{company.name}</TableCell>
                    <TableCell>{company.address || "-"}</TableCell>
                    <TableCell>{company.phone || "-"}</TableCell>
                    <TableCell>{company.email || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </table>
          </div>
        );

      case t("nav.users"):
        return (
          <div className="overflow-auto max-h-[60vh] relative">
            <table className="w-full caption-bottom text-sm">
              <TableHeader>
                <TableRow className="sticky top-0 z-10 bg-white shadow-sm">
                  <TableHead className="bg-white">{t("common.name")}</TableHead>
                  <TableHead className="bg-white">{t("common.email")}</TableHead>
                  <TableHead className="bg-white">{t("pages.users.role")}</TableHead>
                  <TableHead className="bg-white">{t("common.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {modalData.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          user.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {user.is_active ? t("pages.users.active") : t("pages.users.inactive")}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </table>
          </div>
        );

      case t("nav.checkpoints"):
        return (
          <>
            <div className="overflow-auto max-h-[60vh] relative">
              <table className="w-full caption-bottom text-sm">
                <TableHeader>
                  <TableRow className="sticky top-0 z-10 bg-white shadow-sm">
                    <TableHead className="bg-white">{t("common.name")}</TableHead>
                    <TableHead className="bg-white">{t("common.address")}</TableHead>
                    <TableHead className="bg-white">{t("pages.checkpoints.location")}</TableHead>
                    <TableHead className="bg-white">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                {modalData.map((checkpoint) => (
                  <TableRow key={checkpoint.id}>
                    <TableCell className="font-medium">{checkpoint.name}</TableCell>
                    <TableCell>{checkpoint.address || "-"}</TableCell>
                    <TableCell>
                      {checkpoint.latitude && checkpoint.longitude ? (
                        <div className="w-32 h-24 rounded-lg overflow-hidden border border-slate-200">
                          <MapContainer
                            center={[checkpoint.latitude, checkpoint.longitude]}
                            zoom={15}
                            style={{ height: "100%", width: "100%" }}
                            zoomControl={false}
                            dragging={false}
                            touchZoom={false}
                            doubleClickZoom={false}
                            scrollWheelZoom={false}
                          >
                            <GoogleTileLayer />
                            {checkpoint.radius_feet && (
                              <Circle
                                center={[checkpoint.latitude, checkpoint.longitude]}
                                radius={feetToMeters(checkpoint.radius_feet)}
                                pathOptions={{
                                  color: "#0F172A",
                                  fillColor: "#0F172A",
                                  fillOpacity: 0.1,
                                  weight: 2,
                                }}
                              />
                            )}
                            <Marker
                              position={[checkpoint.latitude, checkpoint.longitude]}
                              icon={L.divIcon({
                                className: "custom-marker",
                                html: `<div style="
                                  background-color: #0F172A;
                                  color: white;
                                  width: 24px;
                                  height: 24px;
                                  border-radius: 50%;
                                  display: flex;
                                  align-items: center;
                                  justify-content: center;
                                  font-weight: bold;
                                  font-size: 12px;
                                  border: 2px solid white;
                                  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                                ">📍</div>`,
                                iconSize: [24, 24],
                                iconAnchor: [12, 12],
                              })}
                            >
                              <Popup>
                                <div className="p-2">
                                  <h3 className="font-bold text-slate-900">{checkpoint.name}</h3>
                                  <p className="text-xs font-mono text-slate-500 mt-1">
                                    {checkpoint.latitude.toFixed(6)}, {checkpoint.longitude.toFixed(6)}
                                  </p>
                                </div>
                              </Popup>
                            </Marker>
                          </MapContainer>
                        </div>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {checkpoint.latitude && checkpoint.longitude && (
                        <button
                          onClick={() => setExpandedMap(checkpoint)}
                          className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
                        >
                          <Maximize2 className="h-4 w-4" />
                          Ampliar
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                </TableBody>
              </table>
            </div>
            
            {/* Modal para mapa ampliado */}
            {expandedMap && (
              <Dialog open={!!expandedMap} onOpenChange={() => setExpandedMap(null)}>
                <DialogContent className="max-w-4xl">
                  <DialogHeader>
                    <DialogTitle>{expandedMap.name}</DialogTitle>
                  </DialogHeader>
                  <div className="mt-4">
                    <div className="h-[500px] w-full rounded-lg overflow-hidden border border-slate-200">
                      <MapContainer
                        center={[expandedMap.latitude, expandedMap.longitude]}
                        zoom={15}
                        style={{ height: "100%", width: "100%" }}
                      >
                        <GoogleTileLayer />
                        {expandedMap.radius_feet && (
                          <Circle
                            center={[expandedMap.latitude, expandedMap.longitude]}
                            radius={feetToMeters(expandedMap.radius_feet)}
                            pathOptions={{
                              color: "#0F172A",
                              fillColor: "#0F172A",
                              fillOpacity: 0.1,
                              weight: 2,
                            }}
                          />
                        )}
                        <Marker
                          position={[expandedMap.latitude, expandedMap.longitude]}
                          icon={L.divIcon({
                            className: "custom-marker",
                            html: `<div style="
                              background-color: #0F172A;
                              color: white;
                              width: 32px;
                              height: 32px;
                              border-radius: 50%;
                              display: flex;
                              align-items: center;
                              justify-content: center;
                              font-weight: bold;
                              font-size: 14px;
                              border: 3px solid white;
                              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                            ">📍</div>`,
                            iconSize: [32, 32],
                            iconAnchor: [16, 16],
                          })}
                        >
                          <Popup>
                            <div className="p-2">
                              <h3 className="font-bold text-slate-900">{expandedMap.name}</h3>
                              {expandedMap.description && (
                                <p className="text-sm text-slate-600 mt-1">{expandedMap.description}</p>
                              )}
                              {expandedMap.address && (
                                <p className="text-sm text-slate-600 mt-1">{expandedMap.address}</p>
                              )}
                              <p className="text-xs font-mono text-slate-500 mt-1">
                                {expandedMap.latitude?.toFixed(6)}, {expandedMap.longitude?.toFixed(6)}
                              </p>
                              {expandedMap.radius_feet && (
                                <p className="text-xs text-slate-500 mt-1">
                                  Radio: {expandedMap.radius_feet} pies
                                </p>
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      </MapContainer>
                    </div>
                    <div className="mt-4 text-sm text-slate-600">
                      <p><strong>{t("common.address")}:</strong> {expandedMap.address || t("pages.dashboard.notSpecified")}</p>
                      <p className="mt-1">
                        <strong>Coordenadas:</strong> {expandedMap.latitude?.toFixed(6)}, {expandedMap.longitude?.toFixed(6)}
                      </p>
                      {expandedMap.radius_feet && (
                        <p className="mt-1">
                          <strong>Radio:</strong> {expandedMap.radius_feet} pies
                        </p>
                      )}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </>
        );

      case "Rutas":
        return (
          <div className="overflow-auto max-h-[60vh] relative">
            <table className="w-full caption-bottom text-sm">
              <TableHeader>
                <TableRow className="sticky top-0 z-10 bg-white shadow-sm">
                  <TableHead className="bg-white">Nombre</TableHead>
                  <TableHead className="bg-white">Tipo de Orden</TableHead>
                  <TableHead className="bg-white">Checkpoints</TableHead>
                  <TableHead className="bg-white">Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {modalData.map((route) => (
                  <TableRow key={route.id}>
                    <TableCell className="font-medium">{route.name}</TableCell>
                    <TableCell>
                      {route.order_type === "sequential" ? "Secuencial" : "Aleatorio"}
                    </TableCell>
                    <TableCell>{route.checkpoint_ids?.length || 0}</TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          route.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {route.is_active ? "Activa" : "Inactiva"}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </table>
          </div>
        );

      case t("pages.dashboard.guardsOnShift"):
        return (
          <div className="overflow-auto max-h-[60vh] relative">
            <table className="w-full caption-bottom text-sm">
              <TableHeader>
                <TableRow className="sticky top-0 z-10 bg-white shadow-sm">
                  <TableHead className="bg-white">{t("common.name")}</TableHead>
                  <TableHead className="bg-white">{t("common.email")}</TableHead>
                  <TableHead className="bg-white">{t("pages.companies.name")}</TableHead>
                  <TableHead className="bg-white">{t("pages.dashboard.entryTime")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {modalData.map((guard) => (
                  <TableRow key={guard.user_id}>
                    <TableCell className="font-medium">{guard.user_name}</TableCell>
                    <TableCell>{guard.user_email}</TableCell>
                    <TableCell>{guard.company_name}</TableCell>
                    <TableCell>
                      {guard.clock_in
                        ? formatDateTime(guard.clock_in)
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </table>
          </div>
        );

      case t("pages.dashboard.activeSessions"):
        return (
          <div className="overflow-auto max-h-[60vh] relative">
            <p className="text-sm text-slate-500 mb-3">
              {t("pages.dashboard.activeSessionsHint")}
            </p>
            <table className="w-full caption-bottom text-sm">
              <TableHeader>
                <TableRow className="sticky top-0 z-10 bg-white shadow-sm">
                  <TableHead className="bg-white">{t("pages.dashboard.user")}</TableHead>
                  <TableHead className="bg-white">{t("nav.routes")}</TableHead>
                  <TableHead className="bg-white">{t("pages.dashboard.start")}</TableHead>
                  <TableHead className="bg-white">{t("pages.dashboard.completedCheckpoints")}</TableHead>
                  {canManage() && (
                    <TableHead className="bg-white text-right">{t("common.actions")}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {modalData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={canManage() ? 5 : 4}
                      className="text-center text-slate-500 py-8"
                    >
                      {t("pages.dashboard.noActiveSessions")}
                    </TableCell>
                  </TableRow>
                ) : (
                  modalData.map((session) => {
                    const detail = sessionDetails[session.id] || {};
                    return (
                      <TableRow key={session.id}>
                        <TableCell className="font-medium">
                          {detail.userName ||
                            session.user_name ||
                            t("pages.dashboard.unknown")}
                        </TableCell>
                        <TableCell>
                          {detail.routeName ||
                            session.route_name ||
                            t("pages.dashboard.unknownRoute")}
                        </TableCell>
                        <TableCell>
                          {session.start_time
                            ? formatDateTime(session.start_time)
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {session.completed_checkpoints?.length || 0} /{" "}
                          {detail.totalCheckpoints ||
                            session.total_checkpoints ||
                            0}
                        </TableCell>
                        {canManage() && (
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              disabled={closingSessionId === session.id}
                              onClick={() => handleForceEndSession(session.id)}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              {t("pages.dashboard.closeSession")}
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </table>
          </div>
        );

      case t("pages.dashboard.patrolsToday"):
        return (
          <div className="max-h-[500px] overflow-y-auto relative">
            <table className="w-full caption-bottom text-sm">
              <TableHeader>
                <TableRow className="sticky top-0 z-10 bg-white shadow-sm">
                  <TableHead className="bg-white">{t("pages.dashboard.user")}</TableHead>
                  <TableHead className="bg-white">{t("nav.checkpoints")}</TableHead>
                  <TableHead className="bg-white">{t("common.time")}</TableHead>
                  <TableHead className="bg-white">{t("common.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {modalData.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="font-medium">
                      {log.user_name || t("pages.dashboard.unknown")}
                    </TableCell>
                    <TableCell>{log.checkpoint_name || "-"}</TableCell>
                    <TableCell>
                      {log.timestamp ? formatDateTime(log.timestamp) : "-"}
                    </TableCell>
                    <TableCell>
                      {log.is_manual ? (
                        <span className="px-2 py-1 rounded text-xs bg-orange-100 text-orange-700">
                          {t("pages.dashboard.manual")}
                        </span>
                      ) : log.qr_code ? (
                        <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">
                          QR
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700">
                          GPS
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </table>
          </div>
        );

      default:
        return (
          <div className="text-center py-8 text-slate-500">
            {t("pages.dashboard.viewNotAvailable")}
          </div>
        );
    }
  };

  const statCards = [
    {
      title: t("nav.companies"),
      value: stats?.total_companies || 0,
      icon: Building2,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      title: t("nav.users"),
      value: stats?.total_users || 0,
      icon: Users,
      color: "text-purple-600",
      bg: "bg-purple-50",
    },
    {
      title: t("nav.checkpoints"),
      value: stats?.total_checkpoints || 0,
      icon: MapPin,
      color: "text-green-600",
      bg: "bg-green-50",
    },
    {
      title: t("nav.routes"),
      value: stats?.total_routes || 0,
      icon: Route,
      color: "text-orange-600",
      bg: "bg-orange-50",
    },
    {
      title: t("pages.dashboard.activeSessions"),
      value: stats?.active_sessions || 0,
      icon: Radio,
      color: "text-red-600",
      bg: "bg-red-50",
    },
    {
      title: t("pages.dashboard.guardsOnShift"),
      value: stats?.guards_on_shift || 0,
      icon: Users,
      color: "text-indigo-600",
      bg: "bg-indigo-50",
    },
    {
      title: t("pages.dashboard.patrolsToday"),
      value: stats?.today_patrols || 0,
      icon: ClipboardCheck,
      color: "text-teal-600",
      bg: "bg-teal-50",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col" data-testid="dashboard">
      {/* Header */}
      <div className="flex-shrink-0 mb-4">
        <h1 className="font-heading text-3xl font-bold text-slate-900">{t("pages.dashboard.title")}</h1>
        <p className="text-slate-600 mt-1">
          {t("pages.dashboard.welcome", { name: user?.name })}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="flex-shrink-0 mb-4">
        <div className="grid grid-cols-2 sm:grid-cols-[repeat(auto-fit,minmax(135px,1fr))] gap-3">
          {statCards.map((stat) => (
            <Card
              key={stat.title}
              className="card-hover cursor-pointer transition-all hover:shadow-md"
              onClick={() => handleTileClick(stat.title)}
            >
              <CardContent className="p-3">
                <div className="flex items-center gap-2.5">
                  <div className={`p-1.5 rounded-lg flex-shrink-0 ${stat.bg}`}>
                    <stat.icon className={`h-4 w-4 ${stat.color}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl font-bold leading-tight text-slate-900">{stat.value}</p>
                    <p className="text-[11px] leading-tight text-slate-500 line-clamp-2">{stat.title}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Map and Recent Activity */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 flex-1 min-h-0 mb-6">
        {/* Map */}
        <Card className="xl:col-span-2 flex flex-col min-h-0">
          <CardHeader className="pb-2 flex-shrink-0">
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="h-5 w-5 text-primary" />
              {t("nav.checkpoints")}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col min-h-0 p-6">
            <div className="h-[60vh] min-h-[380px]">
              <CheckpointMap
                checkpoints={checkpoints}
                height="100%"
                interactive={true}
              />
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="flex flex-col min-h-0">
          <CardHeader className="pb-2 flex-shrink-0">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5 text-primary" />
              {t("pages.dashboard.recentLogs")}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col min-h-0">
            <div className="space-y-3 flex-1 overflow-y-auto xl:max-h-[60vh]">
              {!Array.isArray(recentLogs) || recentLogs.length === 0 ? (
                <p className="text-slate-500 text-center py-8">
                  {t("pages.dashboard.noLogs")}
                </p>
              ) : (
                recentLogs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg"
                  >
                    <div
                      className={`w-2 h-2 rounded-full mt-2 ${
                        log.is_manual ? "bg-orange-500" : "bg-green-500"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {t("pages.dashboard.checkpointRegistered")}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatDateTime(log.timestamp)}
                      </p>
                      {log.is_manual && (
                        <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded">
                          {t("pages.dashboard.manual")}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <Card className="flex-shrink-0 mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="h-5 w-5 text-primary" />
            {t("pages.dashboard.systemSummary")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-4xl font-bold text-primary">
                {stats?.total_checkpoints || 0}
              </p>
              <p className="text-slate-600 mt-1">{t("pages.dashboard.configuredPoints")}</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-4xl font-bold text-green-600">
                {stats?.today_patrols || 0}
              </p>
              <p className="text-slate-600 mt-1">{t("pages.dashboard.checkinsToday")}</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-4xl font-bold text-indigo-600">
                {stats?.guards_on_shift || 0}
              </p>
              <p className="text-slate-600 mt-1">{t("pages.dashboard.guardsOnShift")}</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-4xl font-bold text-red-600">
                {stats?.active_sessions || 0}
              </p>
              <p className="text-slate-600 mt-1">{t("pages.dashboard.patrolSessions")}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col p-0">
          <DialogHeader className="sticky top-0 z-20 bg-white border-b px-6 py-4 shadow-sm">
            <DialogTitle>{t("pages.dashboard.detailsOf", { title: modalTitle })}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 px-6 pb-6">
            <div className="mt-4">{renderModalContent()}</div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
